import AddIcon from '@mui/icons-material/Add'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EventIcon from '@mui/icons-material/Event'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import LaunchIcon from '@mui/icons-material/Launch'

// Maps anomaly codes + entity types to action chip definitions.
// Actions deep-link to existing pages and carry intent via query params:
//   - Guru: ?ask=<question>  → auto-sent on landing (handled in GuruChat)
//   - Upload: ?upload=1&file_type_code=<code>  → upload modal pre-selects type
//   - Task create: ?bp=<id>&title=<title>  → create-task drawer pre-fills

const bpFolder = (bpId, folder) => `/bp-list/${bpId}/${folder}`
const filesFolderPath = (bpId) => bpFolder(bpId, 'other')
// Upload route is the dedicated upload page that opens in a new tab.
// `type` accepts a FileType UUID (not a slug). Falls back to no pre-selection.
const uploadPath = (bpId, fileTypeId) =>
  `/files/upload/${bpId}?method=traditional${fileTypeId ? `&type=${encodeURIComponent(fileTypeId)}` : ''}`
const calendarPath = (bpId) => `/calendar?bp=${bpId}`
const accessPath = (bpId) => bpFolder(bpId, 'accessControl')
const guruAsk = (bpId, question) =>
  `${bpFolder(bpId, 'guru')}?ask=${encodeURIComponent(question)}`
const actionsPath = (bpId) => `/actions?bp=${bpId}&status=open`
const tasksHubPath = () => `/tasks?tab=tasks`
// CreateTask reads `bpId` (camelCase) + `title` from the query string.
const taskCreatePath = (bpId, title) =>
  `/tasks/create?bpId=${bpId}${title ? `&title=${encodeURIComponent(title)}` : ''}`
const incidentsPath = (bpId) => `/incidents?bp=${bpId}`
const notesPath = (bpId) => bpFolder(bpId, 'notes')
const inboxPath = (bpId) => bpFolder(bpId, 'inbox')
const filePath = (bpId, fileId) => `/bp-list/${bpId}/source/${fileId}`

const MAX_LABEL_LEN = 28

const humaniseCode = (code) =>
  (code || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const shortName = (name, fallback = 'document') => {
  if (!name) return fallback
  const cleaned = humaniseCode(name)
  if (cleaned.length <= MAX_LABEL_LEN) return cleaned
  return cleaned.slice(0, MAX_LABEL_LEN - 1).trimEnd() + '…'
}

const typeFromContext = (ctx) => ({
  code: ctx?.fileTypeCode || null,
  name: shortName(ctx?.fileTypeName || ctx?.fileTypeCode, 'document'),
  full: humaniseCode(ctx?.fileTypeName || ctx?.fileTypeCode || 'document'),
})

const isTaskManagementHref = (href) =>
  typeof href === 'string' &&
  (/^\/actions(\?|$)/.test(href) ||
    /^\/tasks(\/|\?|$)/.test(href) ||
    /^\/bp-list\/[^/]+\/compliance(\?|$)/.test(href))

const filterTaskManagementActions = (actions, hasTaskManagement) =>
  hasTaskManagement ? actions : actions.filter((a) => !isTaskManagementHref(a.href))

export const actionsForAnomaly = (anomaly, bpId, hasTaskManagement = false) => {
  const ctx = anomaly.context || {}
  const t = typeFromContext(ctx)
  const build = () => {
  switch (anomaly.code) {
    case 'overdue_document':
      return [
        {
          Icon: UploadFileIcon,
          label: `Upload new ${t.name}`,
          href: uploadPath(bpId, ctx?.fileTypeId),
          variant: 'primary',
          title: `Upload a replacement ${t.full}`,
        },
        {
          Icon: AddIcon,
          label: `Schedule ${t.name} renewal`,
          href: taskCreatePath(bpId, `Renew ${t.full}`),
          title: `Create a task: Renew ${t.full}`,
        },
        {
          Icon: AutoAwesomeIcon,
          label: `Why is ${t.name} important?`,
          href: guruAsk(bpId, `Why is the "${t.full}" document required for this building, and what are the consequences of it being overdue?`),
          variant: 'guru',
        },
      ]
    case 'expiring_soon_document':
      return [
        {
          Icon: AddIcon,
          label: `Schedule ${t.name} renewal`,
          href: taskCreatePath(bpId, `Renew ${t.full}`),
          variant: 'primary',
        },
        { Icon: EventIcon, label: 'See in calendar', href: calendarPath(bpId) },
        {
          Icon: AutoAwesomeIcon,
          label: `Renewal options for ${t.name}`,
          href: guruAsk(bpId, `What's the recommended way to renew the "${t.full}" for this building, and who typically does this?`),
          variant: 'guru',
        },
      ]
    case 'missing_compliance_type':
      return [
        {
          Icon: UploadFileIcon,
          label: `Upload ${t.name}`,
          href: uploadPath(bpId, ctx?.fileTypeId),
          variant: 'primary',
        },
        {
          Icon: AutoAwesomeIcon,
          label: `Is ${t.name} required?`,
          href: guruAsk(bpId, `Is a "${t.full}" required for this building under UK legislation? If yes, who is responsible for producing it and how often must it be renewed?`),
          variant: 'guru',
        },
      ]
    case 'open_critical_incident':
      return [
        { Icon: OpenInNewIcon, label: 'Open in Incidents', href: incidentsPath(bpId), variant: 'primary' },
        { Icon: AddIcon, label: 'Create follow-up task', href: taskCreatePath(bpId, 'Follow up: ' + (anomaly.title || 'incident')) },
        {
          Icon: AutoAwesomeIcon,
          label: 'What should I do next?',
          href: guruAsk(bpId, `There is an open critical incident on this building: "${anomaly.title}". What investigations and remedial actions should be taken, and what are my legal obligations?`),
          variant: 'guru',
        },
      ]
    case 'open_fault_report':
      return [
        { Icon: OpenInNewIcon, label: 'Open in Incidents', href: incidentsPath(bpId), variant: 'primary' },
        { Icon: AddIcon, label: 'Create contractor task', href: taskCreatePath(bpId, 'Rectify fault: ' + (anomaly.title || '')) },
      ]
    case 'stale_task':
      return [
        { Icon: OpenInNewIcon, label: 'View in Actions', href: actionsPath(bpId), variant: 'primary' },
      ]
    case 'expiring_subscription':
      return [
        { Icon: OpenInNewIcon, label: 'Renew subscription', href: '/billing-list', variant: 'primary' },
      ]
    case 'dormant_member':
      return [
        { Icon: ManageAccountsIcon, label: 'Review their access', href: accessPath(bpId) },
      ]
    case 'missing_role':
      return [
        { Icon: AddIcon, label: 'Assign someone', href: accessPath(bpId), variant: 'primary' },
        {
          Icon: AutoAwesomeIcon,
          label: 'Who should hold this role?',
          href: guruAsk(bpId, `What are the typical qualifications and duties of the role currently missing on this building, and who is usually best placed to take it on?`),
          variant: 'guru',
        },
      ]
    case 'unanswered_email':
      return [
        { Icon: OpenInNewIcon, label: 'Open inbox', href: inboxPath(bpId), variant: 'primary' },
      ]
    default:
      return [{ Icon: OpenInNewIcon, label: 'View details', href: filesFolderPath(bpId) }]
  }
  }
  return filterTaskManagementActions(build(), hasTaskManagement)
}

export const actionsForComplianceRow = (row, bpId) => {
  const t = { code: row.fileTypeCode, name: shortName(row.fileTypeName || row.fileTypeCode), full: humaniseCode(row.fileTypeName || row.fileTypeCode || 'document') }
  const actions = []
  if (row.state === 'overdue' || row.state === 'missing') {
    actions.push({
      Icon: UploadFileIcon,
      label: `Upload new ${t.name}`,
      href: uploadPath(bpId, row.fileTypeId),
      variant: 'primary',
    })
    actions.push({
      Icon: AddIcon,
      label: `Schedule renewal`,
      href: taskCreatePath(bpId, `Renew ${t.full}`),
    })
  } else if (row.state === 'expiring_soon') {
    actions.push({
      Icon: UploadFileIcon,
      label: `Renew ${t.name} now`,
      href: uploadPath(bpId, row.fileTypeId),
      variant: 'primary',
    })
    actions.push({ Icon: EventIcon, label: 'See in calendar', href: calendarPath(bpId) })
  } else {
    actions.push({ Icon: EventIcon, label: 'See in calendar', href: calendarPath(bpId) })
  }
  actions.push({
    Icon: AutoAwesomeIcon,
    label: `Ask about ${t.name}`,
    href: guruAsk(bpId, `Tell me about the "${t.full}" status for this building. Why does it matter and what should we do next?`),
    variant: 'guru',
  })
  return actions
}

export const actionsForDocumentRow = (row, bpId) => {
  const actions = []
  const isOverdue = row.expirationDate && new Date(row.expirationDate) < new Date()
  if (row.fileId) {
    actions.push({ Icon: OpenInNewIcon, label: 'Open document', href: filePath(bpId, row.fileId) })
  }
  if (isOverdue) {
    const t = shortName(row.fileTypeName || row.fileTypeCode, 'document')
    actions.push({
      Icon: UploadFileIcon,
      label: `Upload new ${t}`,
      href: uploadPath(bpId, row.fileTypeId),
      variant: 'primary',
    })
  }
  return actions
}

export const actionsForPerson = (m, bpId) => {
  const name = m?.name || 'this member'
  const role = m?.relationType
    ? ` (${m.relationType.replace(/_/g, ' ')})`
    : ''
  return [
    {
      Icon: AutoAwesomeIcon,
      label: `Ask Guru about ${name}`,
      href: guruAsk(
        bpId,
        `Tell me about ${name}${role} on this building — when were they added, what is their role and responsibility, and have they been linked to any files, tasks, or incidents recently?`,
      ),
      variant: 'guru',
    },
  ]
}

export const sectionHeaderActions = (sectionKey, bpId, hasTaskManagement = false) => {
  const build = () => {
  switch (sectionKey) {
    case 'overview':
      return [
        {
          Icon: AutoAwesomeIcon,
          label: 'Summarise this building',
          href: guruAsk(bpId, 'Give me a one-paragraph summary of this building covering use, occupancy, key roles, current compliance posture, and any open critical issues.'),
          variant: 'guru',
        },
      ]
    case 'legislation':
      return [
        {
          Icon: AutoAwesomeIcon,
          label: 'Explain my legal duties',
          href: guruAsk(bpId, 'In plain English, what are my legal duties as the dutyholder of this building? Which Acts and Regulations apply, and what specifically must I do?'),
          variant: 'guru',
        },
      ]
    case 'activity':
    case 'recent_events':
      return [
        { Icon: OpenInNewIcon, label: 'Open notes', href: notesPath(bpId) },
      ]
    case 'risk_compliance':
      return [
        { Icon: EventIcon, label: 'Open calendar', href: calendarPath(bpId) },
        {
          Icon: AutoAwesomeIcon,
          label: 'Brief me on risks',
          href: guruAsk(bpId, 'Walk me through the current risk and compliance posture of this building. What should I prioritise this week?'),
          variant: 'guru',
        },
      ]
    case 'people':
      return [
        { Icon: ManageAccountsIcon, label: 'Manage access', href: accessPath(bpId) },
      ]
    case 'documents':
      return [
        { Icon: UploadFileIcon, label: 'Upload document', href: uploadPath(bpId) },
        { Icon: LaunchIcon, label: 'All documents', href: bpFolder(bpId, 'other') },
      ]
    case 'compliance':
      return [
        { Icon: EventIcon, label: 'Open calendar', href: calendarPath(bpId) },
        {
          Icon: AutoAwesomeIcon,
          label: 'Compliance gaps',
          href: guruAsk(bpId, 'List the compliance gaps on this building in priority order, with the legal basis for each and the recommended next step.'),
          variant: 'guru',
        },
      ]
    case 'tasks':
      return [
        { Icon: LaunchIcon, label: 'Tasks hub', href: tasksHubPath() },
      ]
    case 'action_items':
      return [
        { Icon: LaunchIcon, label: 'Open remedials', href: actionsPath(bpId), variant: 'primary' },
      ]
    case 'incidents':
      return [
        { Icon: OpenInNewIcon, label: 'Open Incidents', href: incidentsPath(bpId), variant: 'primary' },
      ]
    case 'units':
      return [
        { Icon: LaunchIcon, label: 'View units', href: bpFolder(bpId, 'units') },
      ]
    case 'forecast':
      return [
        { Icon: EventIcon, label: 'Open calendar', href: calendarPath(bpId), variant: 'primary' },
        {
          Icon: AutoAwesomeIcon,
          label: 'What should I plan?',
          href: guruAsk(bpId, 'List everything due in the next 60 days on this building, in priority order, with a recommended owner for each.'),
          variant: 'guru',
        },
      ]
    case 'reports':
      return [
        { Icon: LaunchIcon, label: 'Open Reporting', href: bpFolder(bpId, 'reporting'), variant: 'primary' },
      ]
    case 'analytics_templates':
    case 'checklist_templates':
      return [
        { Icon: LaunchIcon, label: 'Open Analytics', href: bpFolder(bpId, 'analytics'), variant: 'primary' },
      ]
    case 'compliance_sets':
      return [
        { Icon: LaunchIcon, label: 'Open Compliance', href: `/bp-list/${bpId}/compliance`, variant: 'primary' },
      ]
    case 'bp_audits':
      return [
        { Icon: LaunchIcon, label: 'Open Audit history', href: `/bp-list/${bpId}/reporting?tab=audit`, variant: 'primary' },
        {
          Icon: AutoAwesomeIcon,
          label: 'What does an audit cover?',
          href: guruAsk(bpId, 'I want to run a building audit for an external stakeholder. What inputs do you need and what will the audit produce?'),
          variant: 'guru',
        },
      ]
    case 'building_assets':
      return [
        { Icon: LaunchIcon, label: 'Open files', href: bpFolder(bpId, 'other') },
      ]
    case 'notes':
      return [
        { Icon: OpenInNewIcon, label: 'Open notes', href: notesPath(bpId), variant: 'primary' },
      ]
    case 'emails':
      return [
        { Icon: OpenInNewIcon, label: 'Open inbox', href: inboxPath(bpId) },
      ]
    default:
      return []
  }
  }
  return filterTaskManagementActions(build(), hasTaskManagement)
}

export const pageHeaderActions = (bpId) => [
  {
    Icon: AutoAwesomeIcon,
    label: 'Ask Guru anything',
    href: bpFolder(bpId, 'guru'),
    variant: 'guru',
  },
  { Icon: LaunchIcon, label: 'All files', href: bpFolder(bpId, 'other') },
  { Icon: EventIcon, label: 'Renewal calendar', href: calendarPath(bpId) },
]
